import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { House, ArrowClockwise } from '@phosphor-icons/react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary - Catches JavaScript errors and displays a friendly fallback UI
 * Prevents the entire app from crashing with a white screen
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error for debugging
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);
    
    // TODO: Could send to error reporting service here
    // e.g., Sentry, LogRocket, etc.
  }

  handleGoHome = () => {
    // Clear error state and navigate home
    this.setState({ hasError: false, error: null });
    window.location.href = '/create/record';
  };

  handleRefresh = () => {
    // Clear error state and refresh the page
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback onGoHome={this.handleGoHome} onRefresh={this.handleRefresh} />;
    }

    return this.props.children;
  }
}

/**
 * ErrorFallback - The UI shown when an error occurs
 * Functional component for easier styling with hooks
 */
interface ErrorFallbackProps {
  onGoHome: () => void;
  onRefresh: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ onGoHome, onRefresh }) => {
  // We can't use useTheme here reliably since the error might affect theme context
  // Use CSS variables or hardcoded values that work in both modes
  const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isDark ? '#0a0a0a' : '#ffffff',
        px: 3,
        textAlign: 'center'
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          backgroundColor: isDark ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: '24px',
            color: '#ef4444'
          }}
        >
          !
        </Box>
      </Box>

      {/* Message */}
      <Typography
        sx={{
          fontSize: '20px',
          fontWeight: 600,
          color: isDark ? '#f9fafb' : '#111827',
          mb: 1
        }}
      >
        Something went wrong
      </Typography>

      <Typography
        sx={{
          fontSize: '14px',
          color: isDark ? '#9ca3af' : '#6b7280',
          mb: 4,
          maxWidth: 400
        }}
      >
        We encountered an unexpected error. Please try going home or refreshing the page.
      </Typography>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowClockwise size={18} />}
          onClick={onRefresh}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: '8px',
            px: 3,
            borderColor: isDark ? 'rgba(255,255,255,0.2)' : '#e5e7eb',
            color: isDark ? '#9ca3af' : '#6b7280',
            '&:hover': {
              borderColor: isDark ? 'rgba(255,255,255,0.3)' : '#d1d5db',
              backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'
            }
          }}
        >
          Try Again
        </Button>
        
        <Button
          variant="contained"
          startIcon={<House size={18} />}
          onClick={onGoHome}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            px: 3,
            backgroundColor: isDark ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            '&:hover': {
              backgroundColor: isDark ? '#2563eb' : '#000'
            }
          }}
        >
          Go Home
        </Button>
      </Box>
    </Box>
  );
};

export default ErrorBoundary;

